import { SYNC_CONSTANTS } from '@/shared/constants';
import { setGroupParticipants } from '@/features/chats/model/groupParticipants';
import { setUserInfoMap } from '@/features/users/model';
import { getGroupParticipants } from '@/infrastructure/sdk';
import { setSyncProgress, store } from '@/app/store';
import { asyncPool, calculateProgress, createLogger, yieldToMain } from '@/infrastructure/sync/utils';
import { getUserIdFromJid } from '@/shared/utils';

const { PARTICIPANT_SYNC_BATCH_SIZE } = SYNC_CONSTANTS;

const { log, flushLogs } = createLogger('ParticipantSync');

const ParticipantSync = {
   async fetchFromAPI(groupJid) {
      try {
         const res = await getGroupParticipants(groupJid);
         if (res.statusCode === 200) {
            return res.data.participants || res.data || [];
         }
         return [];
      } catch (err) {
         log('ParticipantSync.fetchFromAPI', `Error: ${err.message}`);
         return [];
      }
   },

   syncToRedux(group, participants, userIds) {
      const { groupJid, ...groupMeta } = group;
      if (groupMeta.participants) {
         delete groupMeta.participants;
      }

      const groupId = getUserIdFromJid(groupJid);

      const participantUsers = participants.map(p => ({ ...p, type: 'user' }));
      participantUsers.forEach(p => userIds.add(p.userId || p.jid));
      store.dispatch(setUserInfoMap(participantUsers));

      store.dispatch(setGroupParticipants({ groupId, participants }));
   },
};

export const syncGroupParticipants = async (
   groups,
   { progressRange = [50, 75], enableProgress = true, config = {} } = {},
) => {
   const dispatch = store.dispatch;
   const { preFetch } = config;
   const shouldReportProgress = enableProgress && preFetch;

   const [startProgress, endProgress] = progressRange;
   const userIds = new Set();

   log('Step 0', '🚀 Starting participant synchronization');
   const overallStartTime = performance.now();

   try {
      if (!groups || groups.length === 0) {
         log('Step 1', 'No groups found to sync participants');
         if (shouldReportProgress) dispatch(setSyncProgress(endProgress));
         return userIds;
      }

      const totalGroups = groups.length;
      let processedGroups = 0;
      log('Step 1', `Found ${totalGroups} groups to sync participants for`);

      await asyncPool(PARTICIPANT_SYNC_BATCH_SIZE, groups, async group => {
         const { groupId, groupJid } = group;
         if (!groupId || !groupJid) return;

         const logId = `SyncGroup-${groupId}`;
         try {
            log(logId, 'Fetching from server');
            const apiParticipants = await ParticipantSync.fetchFromAPI(groupJid);

            if (apiParticipants.length > 0) {
               ParticipantSync.syncToRedux(group, apiParticipants, userIds);
            }
         } catch (error) {
            log(logId, `Error: ${error.message}`);
         } finally {
            processedGroups++;
            if (shouldReportProgress) {
               const progress = calculateProgress(processedGroups - 1, totalGroups, startProgress, endProgress);
               dispatch(setSyncProgress(progress));
            }
            await yieldToMain();
         }
      });

      return userIds;
   } catch (error) {
      log('Error', `syncGroupParticipants Failed: ${error.message}`);
      throw error;
   } finally {
      const totalDuration = ((performance.now() - overallStartTime) / 1000).toFixed(2);
      flushLogs(`[ParticipantSync] SYNC COMPLETE: UI RELEASED in ${totalDuration}s`);
   }
};
